import { useEffect, useState } from "react";
import { Box, Button, Divider, FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import API from "../../../api";

const DEFAULT_RESOLUTION = { dns_ptr_enabled: true, netbios_enabled: true };

const AUTH_PROTOCOLS = ["MD5", "SHA", "SHA224", "SHA256", "SHA384", "SHA512"];
const PRIV_PROTOCOLS = ["DES", "3DES", "AES", "AES192", "AES256"];

// Un profilo = un set di target + le credenziali per interrogarli. Più
// profili permettono di scansionare subnet diverse con credenziali diverse
// (es. una subnet su v2c/community pubblica, un'altra su v3 di un altro
// vendor) invece di un'unica configurazione globale condivisa da tutti i
// target.
const emptyV3 = () => ({ username: "", security_level: "noAuthNoPriv", auth_protocol: "", auth_key: "", priv_protocol: "", priv_key: "", context_name: "" });
const emptyProfile = () => ({ targetsText: "", port: 161, version: "v2c", community: "public", timeout: 2, retries: 1, v3: emptyV3() });

// Converte un profilo grezzo (dal backend, targets come array) nella forma
// usata dal form (targetsText come testo multiriga) — riempie i default per
// i campi assenti, cosi' un profilo salvato prima dell'aggiunta di un campo
// non fa saltare il form.
const toProfileState = (raw) => ({
  ...emptyProfile(),
  ...raw,
  targetsText: (raw?.targets || []).join("\n"),
  v3: { ...emptyV3(), ...(raw?.v3 || {}) },
});

// Un profilo caricato prima dell'introduzione del multi-profilo arriva come
// oggetto singolo "active.snmp", non come lista "active.snmp_profiles" — lo
// trattiamo come il primo (e unico) profilo, cosi' una config esistente non
// sparisce dal form al primo caricamento dopo l'aggiornamento.
const profilesFromLoadedConfig = (loaded) => {
  const list = Array.isArray(loaded?.active?.snmp_profiles) ? loaded.active.snmp_profiles : [];
  if (list.length) return list;
  const legacy = loaded?.active?.snmp;
  return legacy?.targets?.length ? [legacy] : [];
};

const AssetInventorySettings = () => {
  // Active (SNMP) enrichment probes assets on the wire on Orion's behalf — with
  // Orion off there is nothing to enrich for, so the whole block is locked
  // rather than left configurable-but-inert. Passive Resolution (DNS/NetBIOS)
  // doesn't depend on Orion and stays enabled regardless. Defaults to visible
  // while the check is in flight, same convention as asset-detail's own
  // getOrionVisibility() use — nothing flashes locked and then unlocks.
  const [orionVisible, setOrionVisible] = useState(true);
  const enrichmentLocked = !orionVisible;
  const [resolution, setResolution] = useState(DEFAULT_RESOLUTION);
  const [snmpEnabled, setSnmpEnabled] = useState(false);
  const [scanInterval, setScanInterval] = useState(300);
  const [profiles, setProfiles] = useState([emptyProfile()]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    API.getOrionVisibility()
      .then((res) => setOrionVisible(res?.data?.visibility !== false))
      .catch(() => {});
  }, []);

  useEffect(() => {
    API.getAssetInventoryConfig().then((res) => {
      const loaded = res.data || {};
      setResolution({ ...DEFAULT_RESOLUTION, ...(loaded.resolution || {}) });
      setSnmpEnabled(Boolean(loaded.active?.snmp_enabled));
      setScanInterval(loaded.active?.scan_interval_seconds ?? 300);
      const rawProfiles = profilesFromLoadedConfig(loaded);
      setProfiles(rawProfiles.length ? rawProfiles.map(toProfileState) : [emptyProfile()]);
    }).catch(() => {});
  }, []);

  const updateProfile = (index, patch) => {
    setProfiles((current) => current.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };
  const updateProfileV3 = (index, patch) => {
    setProfiles((current) => current.map((p, i) => (i === index ? { ...p, v3: { ...p.v3, ...patch } } : p)));
  };
  const addProfile = () => setProfiles((current) => [...current, emptyProfile()]);
  const removeProfile = (index) => setProfiles((current) => current.filter((_, i) => i !== index));

  const save = async () => {
    setSaving(true);
    setMessage("");
    const payload = {
      resolution,
      active: {
        scan_interval_seconds: Number(scanInterval) || 300,
        snmp_enabled: snmpEnabled,
        snmp_profiles: profiles.map((p) => ({
          targets: p.targetsText.split(/[\n,;]+/).map((t) => t.trim()).filter(Boolean),
          port: Number(p.port) || 161,
          version: p.version,
          community: p.community,
          timeout: Number(p.timeout) || 2,
          retries: Number(p.retries) || 1,
          v3: p.v3,
        })),
      },
    };
    try {
      await API.setAssetInventoryConfig(payload);
      setMessage("Asset inventory configuration saved");
    } catch (err) {
      setMessage(err.response?.data?.error || "Unable to save asset inventory configuration");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Box><Typography sx={{ fontSize: 20, fontWeight: 600, color: "#449095" }}>Asset Inventory</Typography><Typography variant="body2" color="text.secondary">Discovery and enrichment settings for this LECS.</Typography></Box>
      <Box sx={{ backgroundColor: "rgba(69,144,149,0.05)", border: "1px solid rgba(69,144,149,0.1)", borderRadius: 2, p: 3 }}><Typography sx={{ fontSize: 18, fontWeight: 600, color: "#449095", mb: 1 }}>Resolution</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Resolve host identity from network naming services.</Typography><Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap><FormControlLabel control={<Switch checked={Boolean(resolution.dns_ptr_enabled)} onChange={(e) => setResolution((r) => ({ ...r, dns_ptr_enabled: e.target.checked }))} />} label="DNS PTR" /><FormControlLabel control={<Switch checked={Boolean(resolution.netbios_enabled)} onChange={(e) => setResolution((r) => ({ ...r, netbios_enabled: e.target.checked }))} />} label="NetBIOS" /></Stack></Box>

      <Box sx={{ backgroundColor: "rgba(69,144,149,0.05)", border: "1px solid rgba(69,144,149,0.1)", borderRadius: 2, p: 3, opacity: enrichmentLocked ? 0.5 : (snmpEnabled ? 1 : 0.72) }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box><Typography sx={{ fontSize: 18, fontWeight: 600, color: "#449095", mb: 0.5 }}>Active enrichment</Typography><Typography variant="body2" color="text.secondary">Probe active SNMP scans against one or more sets of targets, each with its own credentials.</Typography>
            {enrichmentLocked && (
              <Typography variant="body2" sx={{ mt: 0.5, color: "#f5b642" }}>Requires the Orion package — activate it for this LECS to configure active enrichment.</Typography>
            )}
          </Box>
          <Tooltip title={enrichmentLocked ? "Requires the Orion package" : ""}>
            <span>
              <FormControlLabel sx={{ m: 0 }} control={<Switch checked={snmpEnabled} disabled={enrichmentLocked} onChange={(e) => setSnmpEnabled(e.target.checked)} />} label="Enabled" />
            </span>
          </Tooltip>
        </Stack>
        <Divider sx={{ my: 2 }} />

        <Box sx={{ pointerEvents: enrichmentLocked ? "none" : "auto" }}>
        <TextField label="Scan interval (s)" type="number" size="small" disabled={enrichmentLocked} value={scanInterval} onChange={(e) => setScanInterval(e.target.value)} sx={{ mb: 2, width: 180 }} />

        <Stack spacing={2}>
          {profiles.map((profile, index) => {
            const isV3 = profile.version === "v3";
            const needsAuth = isV3 && profile.v3.security_level !== "noAuthNoPriv";
            const needsPriv = isV3 && profile.v3.security_level === "authPriv";
            return (
              <Box key={index} sx={{ border: "1px solid rgba(217,251,253,0.15)", borderRadius: 2, p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#d9fbfd" }}>Profile {index + 1}</Typography>
                  <IconButton size="small" onClick={() => removeProfile(index)} title="Remove profile"><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Stack>

                <TextField
                  label="Targets (IP or CIDR)" multiline minRows={3} fullWidth sx={{ mb: 2 }}
                  value={profile.targetsText}
                  onChange={(e) => updateProfile(index, { targetsText: e.target.value })}
                  placeholder="10.0.0.1\n192.168.1.0/24"
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                  <TextField select label="Version" value={profile.version} onChange={(e) => updateProfile(index, { version: e.target.value })} sx={{ minWidth: 150 }}>
                    <MenuItem value="v1">v1</MenuItem>
                    <MenuItem value="v2c">v2c</MenuItem>
                    <MenuItem value="v3">v3</MenuItem>
                  </TextField>
                  <TextField label="Port" type="number" value={profile.port} onChange={(e) => updateProfile(index, { port: e.target.value })} />
                  {!isV3 && (
                    <TextField label="Community" value={profile.community} onChange={(e) => updateProfile(index, { community: e.target.value })} />
                  )}
                </Stack>

                {isV3 && (
                  <Box sx={{ mb: 2, pl: { xs: 0, sm: 1 }, borderLeft: { sm: "2px solid rgba(69,144,149,0.3)" } }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#d9fbfd", mb: 1, pl: { sm: 1 } }}>SNMPv3 credentials</Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2, pl: { sm: 1 } }}>
                      <TextField label="Username" value={profile.v3.username} onChange={(e) => updateProfileV3(index, { username: e.target.value })} />
                      <TextField select label="Security level" value={profile.v3.security_level} onChange={(e) => updateProfileV3(index, { security_level: e.target.value })} sx={{ minWidth: 180 }}>
                        <MenuItem value="noAuthNoPriv">noAuthNoPriv</MenuItem>
                        <MenuItem value="authNoPriv">authNoPriv</MenuItem>
                        <MenuItem value="authPriv">authPriv</MenuItem>
                      </TextField>
                    </Stack>
                    {needsAuth && (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: needsPriv ? 2 : 0, pl: { sm: 1 } }}>
                        <TextField select label="Auth protocol" value={profile.v3.auth_protocol} onChange={(e) => updateProfileV3(index, { auth_protocol: e.target.value })} sx={{ minWidth: 150 }}>
                          {AUTH_PROTOCOLS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </TextField>
                        <TextField label="Auth password" type="password" value={profile.v3.auth_key} onChange={(e) => updateProfileV3(index, { auth_key: e.target.value })} />
                      </Stack>
                    )}
                    {needsPriv && (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ pl: { sm: 1 } }}>
                        <TextField select label="Priv protocol" value={profile.v3.priv_protocol} onChange={(e) => updateProfileV3(index, { priv_protocol: e.target.value })} sx={{ minWidth: 150 }}>
                          {PRIV_PROTOCOLS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </TextField>
                        <TextField label="Priv password" type="password" value={profile.v3.priv_key} onChange={(e) => updateProfileV3(index, { priv_key: e.target.value })} />
                      </Stack>
                    )}
                  </Box>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField label="Timeout (s)" type="number" value={profile.timeout} onChange={(e) => updateProfile(index, { timeout: e.target.value })} />
                  <TextField label="Retries" type="number" value={profile.retries} onChange={(e) => updateProfile(index, { retries: e.target.value })} />
                </Stack>
              </Box>
            );
          })}
        </Stack>

        <Button startIcon={<AddIcon />} onClick={addProfile} sx={{ mt: 2 }}>Add profile</Button>
        </Box>
      </Box>

      <Box><Button variant="contained" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save configuration"}</Button>{message && <Typography variant="body2" sx={{ mt: 1 }} color={message.includes("saved") ? "success.main" : "error.main"}>{message}</Typography>}</Box>
    </Stack>
  );
};

export default AssetInventorySettings;
